import { z } from 'zod';
import { parseCalendarDate, purchaseTotals } from '@klothiq/domain';
const money = z.coerce
  .number({ invalid_type_error: 'Enter a valid amount' })
  .finite('Enter a valid amount')
  .min(0, 'Amount cannot be negative')
  .max(9999999999.99)
  .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 0.0001, 'Use at most two decimals');
export const purchaseRecordSchema = z
  .object({
    supplier_id: z.string().uuid('Select a supplier'),
    purchase_date: z.string().refine((v) => !!parseCalendarDate(v), 'Enter a valid purchase date'),
    details_mode: z.enum(['amount_only', 'itemized']),
    entered_amount: z.coerce
      .number({ invalid_type_error: 'Enter a valid amount' })
      .finite('Enter a valid amount')
      .min(0, 'Amount cannot be negative')
      .max(999999999999.99)
      .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 0.0001, 'Use at most two decimals'),
    status: z.enum(['draft', 'pending', 'delivered']),
    delivered_on: z.string(),
    invoice_reference: z.string().trim().max(100),
    notes: z.string().trim().max(1000),
    other_charges: money,
    payment_tracking_mode: z.enum(['untracked', 'tracked']),
    reason: z.string().max(500),
    items: z
      .array(
        z
          .object({
            item_id: z.string().uuid().optional(),
            variant_id: z.string().uuid().nullable().optional(),
            description: z.string().trim().min(1, 'Describe this item').max(300),
            quantity: z.coerce
              .number({ invalid_type_error: 'Enter a valid amount' })
              .int()
              .positive()
              .max(2147483647),
            unit_cost: money,
            discount: money,
          })
          .superRefine((v, c) => {
            if (v.discount > v.quantity * v.unit_cost)
              c.addIssue({
                code: 'custom',
                path: ['discount'],
                message: 'Discount exceeds item price',
              });
          }),
      )
      .max(500),
  })
  .superRefine((v, c) => {
    const total =
      v.details_mode === 'amount_only'
        ? v.entered_amount
        : purchaseTotals(v.items, v.other_charges).total;
    if (v.details_mode === 'itemized' && !v.items.length)
      c.addIssue({ code: 'custom', path: ['items'], message: 'Add at least one item' });
    if ((v.status !== 'draft' && total <= 0) || total > 999999999999.99)
      c.addIssue({
        code: 'custom',
        path: ['entered_amount'],
        message: 'Enter a positive supported total',
      });
    if (
      v.status === 'delivered' &&
      (!parseCalendarDate(v.delivered_on) || v.delivered_on < v.purchase_date)
    )
      c.addIssue({
        code: 'custom',
        path: ['delivered_on'],
        message: 'Delivery date must be on or after purchase date',
      });
  });
export type PurchaseRecordValues = z.infer<typeof purchaseRecordSchema>;
